'use client';

import React, { useState, type FC } from 'react';
import { useProfile } from '../queries/useProfileQueries';
import { useUiMotionEnabled } from '../hooks/useMotion';
import { useCountUp } from '../hooks/useCountUp';

interface CreditsChangeFlashProps {
  amount: number;
  /** How to render the number — `Credits` passes its full or compact formatter. */
  format: (amount: number) => string;
}

type FlashState = {
  amount: number;
  /** Profile was loaded when `amount` was recorded — only then is it a real balance. */
  tracked: boolean;
  direction: 'up' | 'down' | null;
  /** Bumped on every animated change so the span remounts and the animation restarts. */
  seq: number;
};

/** Balance readout that counts from the old value to the new one, flashing green on
 *  growth and red on a spend, so both the SIZE and the direction of a change are
 *  readable. The count-up is `useCountUp`, shared with the spark balance.
 *
 *  Mounted only from `<Credits animateChange />`, i.e. inside the authed provider
 *  tree — `useProfile` needs both `ApiProvider` and `QueryClientProvider`.
 *
 *  Two silent cases, both required — and they silence the count-up as well as the
 *  flash: the very first paint never animates, and neither does any change made while
 *  the profile query has no data — screens render `profile?.balanceInCredits ?? 0` in
 *  that window, so a load or a cache reset would otherwise blink red down to zero and
 *  green back up, or count there. A genuine 0 (spent everything, first top-up) still
 *  animates, because by then the profile is loaded. */
export const CreditsChangeFlash: FC<CreditsChangeFlashProps> = ({ amount, format }) => {
  const { data: profile } = useProfile();
  const tracked = profile !== undefined;
  // The flash is interface feedback, not a game reel: only the OS preference silences it.
  const motionEnabled = useUiMotionEnabled();

  const [state, setState] = useState<FlashState>({ amount, tracked, direction: null, seq: 0 });

  if (state.amount !== amount || state.tracked !== tracked) {
    const changed = state.amount !== amount;
    const direction = changed && tracked && state.tracked ? (amount > state.amount ? 'up' : 'down') : null;
    setState({ amount, tracked, direction, seq: direction ? state.seq + 1 : state.seq });
  }

  const animationClass =
    motionEnabled && state.direction === 'up'
      ? 'animate-shot-credits-up'
      : motionEnabled && state.direction === 'down'
        ? 'animate-shot-credits-down'
        : '';

  // Same gate as the flash: no travelling number until the balance is a real one.
  const displayed = useCountUp(amount, tracked && state.tracked);

  return (
    <span key={state.seq} data-credits-value className={`inline-block ${animationClass}`}>
      {format(displayed)}
    </span>
  );
};
